export const DEFAULT_TICKET_RECEIPT_SUBJECT = "[{{ticket.id}}] We received your request: {{ticket.subject}}";

export const DEFAULT_TICKET_RECEIPT_BODY = `Dear {{ticket.requester.name}},

A ticket has been created in our Helpdesk:

[{{ticket.id}}] {{ticket.subject}}

Our team will review the ticket and follow up with you as soon as possible.

To add a comment, simply reply to this email. You can also view and update the ticket using the link below:

{{ticket.url}}

Sincerely,
{{helpdesk_name}}`;

export const DEFAULT_TICKET_CC_SUBJECT = "[{{ticket.id}}] Conversation update: {{ticket.subject}}";

export const DEFAULT_TICKET_CC_BODY = `Hello,

You are copied on this Next-Cart support request:

[{{ticket.id}}] {{ticket.subject}}

{{message.author.name}} wrote:

{{message.body}}

Open the ticket or reply to this email to join the conversation:

{{ticket.url}}

Sincerely,
{{helpdesk_name}}`;

export const TICKET_RECEIPT_SHORTCODES = [
  { token: "{{ticket.id}}", label: "Ticket ID", description: "The formatted ticket number, for example NC-010001." },
  { token: "{{ticket.subject}}", label: "Ticket subject", description: "The subject of the customer's request." },
  { token: "{{ticket.url}}", label: "Ticket URL", description: "The customer's secure ticket page." },
  { token: "{{ticket.requester.name}}", label: "Requester name", description: "The customer's display name." },
  { token: "{{ticket.requester.email}}", label: "Requester email", description: "The customer's email address." },
  { token: "{{mailbox.name}}", label: "Mailbox name", description: "The internal mailbox name." },
  { token: "{{helpdesk_name}}", label: "Sender name", description: "The sender name configured for this mailbox." },
] as const;

export const TICKET_CC_SHORTCODES = [
  ...TICKET_RECEIPT_SHORTCODES,
  { token: "{{cc.email}}", label: "Cc email", description: "The email address receiving this collaborator notification." },
  { token: "{{message.author.name}}", label: "Message author", description: "The name of the customer or staff member who wrote the update." },
  { token: "{{message.body}}", label: "Message body", description: "The plain-text body of the public ticket update." },
] as const;

export interface TicketReceiptTemplateContext {
  ticketId: string;
  ticketSubject: string;
  ticketUrl: string;
  requesterName: string;
  requesterEmail: string;
  mailboxName: string;
  helpdeskName: string;
}

export interface TicketCcTemplateContext extends TicketReceiptTemplateContext {
  ccEmail: string;
  messageAuthorName: string;
  messageBody: string;
}

const shortcodePattern = /{{\s*([^{}]+?)\s*}}/g;

function unsupportedShortcodes(template: string, shortcodes: ReadonlyArray<{ token: string }>): string[] {
  const supported = new Set(shortcodes.map(({ token }) => token.slice(2, -2)));
  const unsupported = new Set<string>();
  for (const match of template.matchAll(shortcodePattern)) {
    const key = match[1].trim();
    if (!supported.has(key)) unsupported.add(`{{${key}}}`);
  }
  return [...unsupported];
}

export function unsupportedTicketReceiptShortcodes(template: string): string[] {
  return unsupportedShortcodes(template, TICKET_RECEIPT_SHORTCODES);
}

export function unsupportedTicketCcShortcodes(template: string): string[] {
  return unsupportedShortcodes(template, TICKET_CC_SHORTCODES);
}

function receiptValues(context: TicketReceiptTemplateContext): Record<string, string> {
  return {
    "ticket.id": context.ticketId,
    "ticket.subject": context.ticketSubject,
    "ticket.url": context.ticketUrl,
    "ticket.requester.name": context.requesterName,
    "ticket.requester.email": context.requesterEmail,
    "mailbox.name": context.mailboxName,
    helpdesk_name: context.helpdeskName,
  };
}

function render(template: string, values: Record<string, string>): string {
  return template.replace(shortcodePattern, (match, rawKey: string) => values[rawKey.trim()] ?? match);
}

export function renderTicketReceiptTemplate(template: string, context: TicketReceiptTemplateContext): string {
  return render(template, receiptValues(context));
}

export function renderTicketCcTemplate(template: string, context: TicketCcTemplateContext): string {
  return render(template, {
    ...receiptValues(context),
    "cc.email": context.ccEmail,
    "message.author.name": context.messageAuthorName,
    "message.body": context.messageBody,
  });
}
